// Prompts del sistema.
//
// Todo lo que se le dice al modelo se edita aquí. Cada prompt son dos piezas:
// `sistema` (el papel, las reglas y el formato de salida) y `usuario` (los datos
// del caso). El orden no es estético: el caché de los proveedores solo acierta si
// el prefijo es idéntico entre llamadas, así que una variable en `sistema` lo
// invalida siempre — y esta pantalla lo avisa mientras se escribe.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, ErrorBanner, HelpBox, PageHeader, Spinner } from "../../components/Layout";
import { PromptsAPI, type DiagnosticoCache, type PromptEntry } from "../../api/endpoints";

const TOKENS_MINIMOS = 64;

/** Mismo cálculo que el servidor, para avisar sin esperar a guardar. */
function diagnosticar(sistema: string): DiagnosticoCache {
  const variables = Array.from(new Set(sistema.match(/\{[a-zA-Z_][a-zA-Z0-9_]*\}/g) ?? [])).sort();
  const tokens = Math.round(sistema.length / 4);
  const motivos: string[] = [];
  if (variables.length) motivos.push(`contiene variables: ${variables.join(", ")}`);
  if (tokens < TOKENS_MINIMOS) motivos.push(`es demasiado corto (${tokens} tokens aprox.)`);
  return { tokens_aprox: tokens, variables_en_sistema: variables, cacheable: motivos.length === 0, motivos };
}

function BadgeCache({ d }: { d: DiagnosticoCache }) {
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded border ${
        d.cacheable
          ? "bg-estado-ok/10 text-estado-ok border-estado-ok/30"
          : "bg-estado-warning/15 text-estado-serio border-estado-warning/40"
      }`}
      title={d.cacheable ? "El proveedor podrá reutilizar este bloque entre llamadas" : d.motivos.join(" · ")}
    >
      {d.cacheable ? `cacheable · ~${d.tokens_aprox} tokens` : "rompe el caché"}
    </span>
  );
}

function TarjetaPrompt({ p }: { p: PromptEntry }) {
  const qc = useQueryClient();
  const [sistema, setSistema] = useState(p.vigente.sistema);
  const [usuario, setUsuario] = useState(p.vigente.usuario);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    setSistema(p.vigente.sistema);
    setUsuario(p.vigente.usuario);
  }, [p.vigente.sistema, p.vigente.usuario]);

  const diag = useMemo(() => diagnosticar(sistema), [sistema]);
  const sinUsar = p.variables.filter((v) => !usuario.includes(v) && !sistema.includes(v));
  const sucio = sistema !== p.vigente.sistema || usuario !== p.vigente.usuario;

  const guardar = useMutation({
    mutationFn: (restaurar: boolean) =>
      PromptsAPI.save(p.clave, restaurar ? "" : sistema, restaurar ? "" : usuario),
    onSuccess: (r) => {
      setOk(r.mensaje);
      qc.invalidateQueries({ queryKey: ["prompts"] });
      setTimeout(() => setOk(null), 3000);
    },
  });

  return (
    <Card className="mb-5">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className="text-base font-semibold text-tinta">{p.titulo}</h3>
        <code className="text-[11px] font-mono text-tinta-3">{p.clave}</code>
        <span
          className={`text-[11px] px-2 py-0.5 rounded border ${
            p.personalizado
              ? "bg-marca/10 text-marca border-marca/30"
              : "bg-plano text-tinta-3 border-grid"
          }`}
        >
          {p.personalizado ? "personalizado" : "de fábrica"}
        </span>
      </div>
      <p className="text-sm text-tinta-3 mb-3">{p.descripcion}</p>

      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="etiqueta mb-0">
            Bloque de sistema — el papel, las reglas y el formato de salida
          </label>
          <BadgeCache d={diag} />
        </div>
        <textarea
          className="input font-mono text-xs leading-relaxed"
          rows={10}
          value={sistema}
          onChange={(e) => setSistema(e.target.value)}
          placeholder={p.default.sistema}
        />
        {!diag.cacheable && (
          <p className="mt-1 text-xs text-estado-serio">
            Este bloque {diag.motivos.join(" y ")}. El proveedor no podrá reutilizarlo entre
            llamadas, así que cada análisis pagará la entrada completa. Mueve las variables al
            bloque de datos.
          </p>
        )}
      </div>

      <div className="mb-3">
        <label className="etiqueta">Bloque de datos — lo que cambia en cada llamada</label>
        <textarea
          className="input font-mono text-xs leading-relaxed"
          rows={6}
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          placeholder={p.default.usuario}
        />
      </div>

      {p.variables.length > 0 && (
        <p className="text-xs text-tinta-3 mb-3">
          Variables disponibles:{" "}
          {p.variables.map((v) => (
            <code
              key={v}
              className={`font-mono mr-1 ${sinUsar.includes(v) ? "text-estado-serio" : "text-marca"}`}
              title={sinUsar.includes(v) ? "Declarada pero no usada en este prompt" : ""}
            >
              {v}
            </code>
          ))}
          {sinUsar.length > 0 && (
            <span className="text-estado-serio"> · las marcadas no aparecen en tu texto</span>
          )}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn" disabled={guardar.isPending || !sucio} onClick={() => guardar.mutate(false)}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </button>
        <button
          className="btn-sec"
          disabled={guardar.isPending || !p.personalizado}
          onClick={() => guardar.mutate(true)}
        >
          Volver al de fábrica
        </button>
        {guardar.isPending && <Spinner />}
        {ok && <span className="text-sm text-estado-ok">{ok}</span>}
      </div>
      {guardar.isError && (
        <div className="mt-3"><ErrorBanner message={(guardar.error as Error).message} /></div>
      )}
    </Card>
  );
}

export default function PromptsPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ["prompts"], queryFn: PromptsAPI.list });

  const porGrupo = useMemo(() => {
    const mapa: Record<string, PromptEntry[]> = {};
    for (const p of data ?? []) (mapa[p.grupo] ??= []).push(p);
    return mapa;
  }, [data]);

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <PageHeader
        title="Prompts"
        subtitle="Lo que el sistema le dice al modelo, editable sin tocar el código."
      />

      <HelpBox title="Por qué cada prompt son dos bloques">
        <p>
          El <b>bloque de sistema</b> lleva el papel que debe adoptar el modelo, las reglas y el
          formato de la respuesta. El <b>bloque de datos</b> lleva lo del caso concreto: la
          pregunta, la encuesta, las fechas y las respuestas.
        </p>
        <p>
          La separación no es cosmética. Los proveedores cobran mucho menos por la parte de la
          petición que ya vieron, pero solo la reconocen si el principio es <b>idéntico</b> entre
          llamadas. Una variable en el bloque de sistema lo cambia cada vez y anula esa rebaja
          sin que nada falle: solo cuesta más. Por eso cada bloque lleva su aviso.
        </p>
        <p>
          Guardar los dos bloques vacíos devuelve el prompt al valor de fábrica. Puedes ver ese
          valor en gris dentro del campo cuando lo dejas en blanco.
        </p>
      </HelpBox>

      {isLoading && (
        <div className="flex items-center gap-2 text-tinta-3"><Spinner /> Cargando prompts…</div>
      )}
      {error && <ErrorBanner message={(error as Error).message} />}

      {Object.entries(porGrupo).map(([grupo, prompts]) => (
        <div key={grupo} className="mb-6">
          <h2 className="text-lg font-semibold text-tinta mb-3">{grupo}</h2>
          {prompts.map((p) => (
            <TarjetaPrompt key={p.clave} p={p} />
          ))}
        </div>
      ))}
    </div>
  );
}
